import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { Order } from "../../../Models/order";
import { OrderService } from "../../../Services/order.service";
import { ProductService } from "../../../Services/product.service";
import { AccountService } from "../../../Services/account.service";
import { MobileOrder } from "../../../Models/mobileOrder";
import { FormControl } from "@angular/forms";
import { ProjectTheme } from "src/app/Models/ProjectTheme";
import { Commande, DropdownOption } from "admin-common";
import { ActivatedRoute, Router } from "@angular/router";
import { mapOrderState, translateOrderState } from "../../../Models/order";
import { formatDate } from "@angular/common";
import { TranslateService } from "@ngx-translate/core";
import { LanguageService } from "../../../Services/language.service";
import { translateCode } from "../../../shared/utils/translate-code";

@Component({
    selector: "app-orders",
    templateUrl: "./orders.component.html",
    styleUrls: ["./orders.component.scss"],
    standalone: false
})
export class OrdersComponent implements OnInit, OnDestroy {
  commandes: Commande[] = [];
  bank: ProjectTheme = "WUnivers";
  orders: Order[] = [];
  //orders: Order[] = [];
  mobileOrders: MobileOrder[] = [];
  selectedStatus = new FormControl();
  pageIndex = 1;
  pageSize = 5;
  pageSizeOptions: number[] = [5, 10, 20, 50];
  totalElements = 0;
  nbPages: number = 1;
  clientId?: number;

  /** order states offered by the status filter ("all statuses" is added by lib-list-commandes) */
  filterByStatusList = [
    "NOT_PAID",
    "CONFIRMED",
    "NOT_PAID_PREPARED",
    "NOT_PAID_IN_DELIVERY",
    "PAID_DELIVERED",
    "PARTIALLY_DELIVERED",
    "NOT_PAID_RETURNED",
    "CANCELED",
  ];

  /** options of the status filter, labelled in the current language */
  statusOptions: DropdownOption[] = [];

  /** searched order number, as typed ("ws2276" or "2276") */
  searchQuery = "";
  /** creation date range (yyyy-MM-dd, '' : no limit) */
  startDate = "";
  endDate = "";
  /** true while the orders are reloaded: lib-list-commandes shows skeleton rows */
  loading = false;

  private search$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  private ordersSubscription?: Subscription;

  displayedColumns: string[] = [
    "id",
    "creationDate",
    "totalPrice",
    "state",
    "action",
  ];

  constructor(
    private orderService: OrderService,
    private productService: ProductService,
    private accountService: AccountService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private languageService: LanguageService,
  ) {}

  ngOnInit(): void {
    // the search field emits on every keystroke: reload once the typing pauses
    this.search$
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        this.searchQuery = query;
        this.pageIndex = 1;
        this.getAllOrders();
      });
    this.getAllOrders();
  }

  ngOnDestroy(): void {
    this.ordersSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** True when a search, a status or a creation date filter is applied */
  get hasActiveFilter(): boolean {
    return (
      !!this.searchQuery.trim() ||
      !!this.selectedStatus.value ||
      !!this.startDate ||
      !!this.endDate
    );
  }

  /** Search, creation date range and status filters sent with the orders request */
  private get filters() {
    return {
      startDate: this.startDate,
      endDate: this.endDate,
      // the list shows the orders as "ws2276": the backend searches the numeric id only
      search: this.searchQuery.trim().replace(/^ws/i, ""),
    };
  }

  /** Status filter options (the page is re-created when the language changes). */
  private buildStatusOptions(): DropdownOption[] {
    return this.filterByStatusList.map((state) => ({
      label: translateCode(
        this.translate,
        "shared.states",
        state,
        mapOrderState,
      ),
      value: state,
    }));
  }

  /**
   * @description reload the first page of orders with the selected status
   * @param status the selected order state ('' : every status)
   */
  onStatusChange(status: string) {
    this.selectedStatus.setValue(status);
    this.pageIndex = 1;
    this.getAllOrders();
  }

  /**
   * @description reload the first page of orders matching the searched order number (debounced)
   * @param query the text typed in the search field
   */
  onSearchChange(query: string) {
    this.search$.next(query);
  }

  /**
   * @description reload the first page of orders created in the selected date range
   * @param range creation date range (yyyy-MM-dd, '' : no limit)
   */
  onDateChange(range: { startDate: string; endDate: string }) {
    this.startDate = range.startDate;
    this.endDate = range.endDate;
    this.pageIndex = 1;
    this.getAllOrders();
  }

  getAllOrders() {
    // a newer filter replaces the pending request: its response must not overwrite the list
    this.ordersSubscription?.unsubscribe();
    this.loading = true;
    this.ordersSubscription = this.orderService
      .getByClientId(
        this.pageIndex - 1,
        this.pageSize,
        "id",
        "desc",
        this.selectedStatus.value ? this.selectedStatus.value : "",
        this.filters,
      )
      .subscribe({
        next: (response) => {
          this.loading = false;
          const pageOfOrders = response.content;
          this.totalElements = response.totalElements;

          if (!pageOfOrders) {
            console.warn("⚠️ Pas de body dans la réponse !");
            return;
          }

          if (!this.statusOptions.length) {
            this.statusOptions = this.buildStatusOptions();
          }
          this.orders = pageOfOrders;
          this.commandes = this.orders.map((order) => ({
            label: "ws" + order.id,
            state: translateOrderState(this.translate, order.state),
            price: order.totalTTC,
            date: this.formatOrderDate(order.createdAt),
            //folder:'',
            //folderState: order.folderState,
            imgProducts: Array.isArray(order.image)
              ? [...order.image]
              : [order.image],
          }));

          // this.totalElements = pageOfOrders.totalElements;
          this.nbPages = Math.ceil(this.totalElements / this.pageSize);
          if (this.nbPages === 0) this.nbPages = 1;
          /*
        this.dataSource = new MatTableDataSource<Order>(this.orders);
        this.dataSource.sort = this.sort!;*/
        },
        error: () => (this.loading = false),
      });
  }

  /** Order date in the current language (the page is re-created when the language changes). */
  private formatOrderDate(date: Date | string | undefined): string {
    return date
      ? formatDate(
          date,
          "dd MMMM yyyy",
          this.languageService.isArabic ? "ar-DZ" : "fr-FR",
        )
      : "";
  }

  openComplainingPopUp() {}
  getAllOrdersOnMobile() {
    this.orderService
      .getAllOrdersInMobile(
        this.pageIndex - 1,
        this.pageSize,
        "id",
        "desc",
        this.selectedStatus.value ? this.selectedStatus.value : "",
      )
      .subscribe((ord) => {
        this.mobileOrders = ord.body!.content;
      });
  }

  retrieveOrders(pageIndex: number) {
    this.orderService
      .getByClientId(
        pageIndex - 1,
        this.pageSize,
        "id",
        "desc",
        this.selectedStatus.value,
        this.filters,
      )
      .subscribe((response) => {
        const pageOfOrders = response.content;

        if (pageOfOrders && response.totalElements != 0) {
          this.orders = pageOfOrders;
          this.commandes = this.orders.map((order) => ({
            label: "ws" + order.id,
            state: translateOrderState(this.translate, order.state),
            price: order.totalTTC,
            date: this.formatOrderDate(order.createdAt),
            //folder:'',
            //folderState: order.folderState,
            imgProducts: Array.isArray(order.image)
              ? [...order.image]
              : [order.image],
          }));
          // this.dataSource = new MatTableDataSource(pageOfOrders.content);
          this.nbPages = Math.ceil(this.totalElements / this.pageSize);
          if (this.nbPages === 0) this.nbPages = 1;
        }
      });
  }

  incrementPageIndex() {
    if (this.pageIndex < this.nbPages) {
      const page = this.pageIndex + 1;
      this.retrieveOrders(page);
      this.pageIndex = this.pageIndex + 1;
    } else;
  }

  decrementPageIndex() {
    if (this.pageIndex > 1) {
      this.pageIndex--;
      this.retrieveOrders(this.pageIndex);
    }
  }

  verifyInputPageIndex() {
    this.retrieveOrders(this.pageIndex);
    if (this.pageIndex > this.nbPages || this.pageIndex < 1) {
      this.pageIndex = 1;
      this.retrieveOrders(this.pageIndex);
    }
  }
  handleOrderClick(item: Commande) {
    if (item) {
      const orderId = parseInt(item.label.replace("ws", ""), 10);

      if (!isNaN(orderId)) {
        this.router.navigate([orderId], { relativeTo: this.route });
      } else {
        console.warn("⚠️ ID de commande invalide :", item.label);
      }
    }
  }

  /**
   * @description reload the orders when the page changes
   * @param page the selected current page
   */
  onPageChange(page: number) {
    this.pageIndex = page;
    this.getAllOrders();
    this.scrollToTheTop();
  }

  /**
   * @description reload the orders when the items per page changes
   * @param items the number of items per page
   * @returns void
   */
  onItemPerPageChange(items: number) {
    this.pageSize = items;
    this.pageIndex = 1; // Reset to the first page when items per page changes
    this.getAllOrders();
    this.scrollToTheTop();
  }

  /**
   * scroll to the top of the page for a better UX
   */
  private scrollToTheTop(): void {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  }
}
